// This file handles client creation and sets up tracking for analytics.
// It differs from the original as it is now the first step in the booking flow,
// and must handle the creation of a client and the conditional addition of their address.

/* eslint-disable complexity */
import {
    useState, useRef, ReactNode
} from 'react';

import { useMutation } from '@apollo/client';
// eslint-disable-next-line import/no-extraneous-dependencies
import * as braze from '@braze/web-sdk';
import styled from '@emotion/styled';
import {
    colors, formatPhone, isValidPhone, trimPhone, BRAZE_EVENT, heapTrack,
    BRAZE_SUBSCRIPTION_GROUP, hasFullAddressChanged, FullAddress
} from '@modernanimal/common';
import { validNameCharacters, validateName } from '@modernanimal/common/src/utils/clientOnboarding';
import Modal from '@modernanimal/common/src/web/atoms/Modal';
import PlacesInputField, { PlacesInputFieldRefType } from '@modernanimal/common/src/web/PlacesInputField';
import { validate } from 'email-validator';
import posthog from 'posthog-js';
import { useNavigate } from 'react-router-dom';
import { useBreakpoint } from 'use-breakpoint';

// Import the standard queries instead of creating custom ones
import { CreateClientMutation } from '../__generated__/graphql';
import { CREATE_CLIENT } from '../clientOnboarding/clientOnboarding.v3.gql';
import InputField from '../components/InputField';
import PrimaryButton from '../components/PrimaryButton';
import { useBrazeContext } from '../context/BrazeContext';
import Fonts from '../fonts';
import { BREAKPOINTS } from '../helpers';
import Icons from '../icons';
import useWebBookingStore, { ClientStoreType } from '../store';

type Props = {
    locationShowCreditCardStep: boolean;
    googleMapsisLoaded: boolean;
    goToNextStep: ()=> void;
};

type NameVariant = 'first' | 'last';

const TestClientOnboarding = ({ locationShowCreditCardStep, googleMapsisLoaded, goToNextStep }: Props) => {
    const {
        client, updateClient
    } = useWebBookingStore((state) => ({
        client: state.client,
        updateClient: state.setClient,
    }));

    const navigate = useNavigate();
    const isBrazeInitialized = useBrazeContext();
    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';

    const [firstName, setFirstName] = useState<string>(client.firstName);
    const [lastName, setLastName] = useState<string>(client.lastName);
    const [email, setEmail] = useState<string>(client.email);
    const [phoneNumber, setPhoneNumber] = useState<string>(formatPhone(client.phoneNumber || ''));
    const [fullAddress, setFullAddress] = useState<FullAddress>(client.fullAddress);
    const [selectedAddress, setSelectedAddress] = useState<Pick<google.maps.places.PlacePrediction, 'text' | 'placeId'> | null>(
        (client.fullAddress.text && client.fullAddress.placeId)
            ? { text: client.fullAddress.text, placeId: client.fullAddress.placeId }
            : null
    );

    const [firstNameIsValid, setFirstNameIsValid] = useState<boolean | null>(client.firstName ? true : null);
    const [lastNameIsValid, setLastNameIsValid] = useState<boolean | null>(client.lastName ? true : null);
    const [emailIsValid, setEmailIsValid] = useState<boolean | null>(client.email ? true : null);
    const [phoneNumberIsValid, setPhoneNumberIsValid] = useState<boolean | null>(client.phoneNumber ? true : null);
    const [addressIsValid, setAddressIsValid] = useState<boolean | null>(client.fullAddress.address ? true : null);

    const [isWhyInfoModalOpen, setIsWhyInfoModalOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const placesInputFieldRef = useRef<PlacesInputFieldRefType>();

    // Add both mutations we need, just like PhoneVerification
    const [createClient] = useMutation(CREATE_CLIENT);

    const changeFirstName = (name: string) => {
        if (validNameCharacters(name)) setFirstName(name);
        if (name.length >= 2) setFirstNameIsValid(true);
        if (firstNameIsValid && name.length < 2) setFirstNameIsValid(null);
    };

    const changeLastName = (name: string) => {
        if (validNameCharacters(name)) setLastName(name);
        if (name.length >= 2) setLastNameIsValid(true);
        if (lastNameIsValid && name.length < 2) setLastNameIsValid(null);
    };

    const changeName = (variant: NameVariant) => (name: string) => {
        if (variant === 'first') {
            changeFirstName(name);
        } else if (variant === 'last') {
            changeLastName(name);
        }
    };

    const changeEmail = () => (email: string) => {
        const trimmedEmail = email.trim();

        setEmail(trimmedEmail.toLowerCase());
        if (validate(trimmedEmail.toLowerCase())) setEmailIsValid(true);
        if (emailIsValid && !validate(trimmedEmail.toLowerCase())) setEmailIsValid(null);
    };

    const changePhoneNumber = (phoneNumber: string) => {
        if (trimPhone(phoneNumber).length <= 10) {
            const formattedPhoneNumber = formatPhone(phoneNumber);
            setPhoneNumber((prevPhoneNumber) => {
                const updatedPhoneNumber = (prevPhoneNumber === formattedPhoneNumber
                    && phoneNumber.length < prevPhoneNumber.length)
                    ? phoneNumber
                    : formattedPhoneNumber;

                // Validate phone number when it reaches 10 digits
                const trimmedPhone = trimPhone(updatedPhoneNumber);
                if (trimmedPhone.length === 10) {
                    setPhoneNumberIsValid(isValidPhone(updatedPhoneNumber));
                } else if (trimmedPhone.length < 10 && phoneNumberIsValid) {
                    // Reset validation if user deletes digits after validation
                    setPhoneNumberIsValid(null);
                }

                return updatedPhoneNumber;
            });
        }
    };

    const validatePhoneNumber = () => {
        if (phoneNumber) {
            setPhoneNumberIsValid(isValidPhone(phoneNumber));
        } else {
            setPhoneNumberIsValid(null);
        }
    };

    const validateNames = (variant: NameVariant) => {
        if (variant === 'first') {
            const isValidFirstName = !!validateName(firstName);
            setFirstNameIsValid(isValidFirstName);
        } else if (variant === 'last') {
            const isValidLastName = !!validateName(lastName);
            setLastNameIsValid(isValidLastName);
        }
    };

    const createNewClient = async () => {
        try {
            const result = await createClient({
                variables: {
                    input: {
                        firstName,
                        lastName,
                        email,
                        phoneNumber: trimPhone(phoneNumber),
                        address: fullAddress.address,
                        apartment: fullAddress.apartment,
                        city: fullAddress.city,
                        state: fullAddress.state,
                        zipcode: fullAddress.zipcode
                    }
                }
            });

            if (!result.data) {
                throw new Error('No data returned from createClient mutation');
            }

            await processClientData(result.data.createClient);
            return true;
        } catch (error) {
            console.warn('Error creating client:', error);
            setLoading(false);
            return navigate('/download-app');
        }
    };

    const processClientData = (clientData: CreateClientMutation['createClient']) => {
        // Update client in store
        const updatedClient: ClientStoreType = {
            ...client,
            id: clientData.id,
            firstName,
            lastName,
            email,
            phoneNumber: trimPhone(phoneNumber),
            token: clientData.token,
            household: {
                id: clientData.household?.id,
                signupLocationId: clientData.household?.derivedLocation.id,
                timezone: clientData.household?.derivedLocation.region.timezone
            },
            fullAddress: {
                ...fullAddress,
                text: selectedAddress?.text || { matches: [], text: '' },
                placeId: selectedAddress?.placeId || ''
            }
        };

        // Track analytics - same as in PhoneVerification
        if (clientData.household?.id) {
            // Identify in PostHog
            posthog.identify(clientData.household.id);

            if (isBrazeInitialized) {
                // Identify in Braze
                braze.changeUser(clientData.id);
                braze.logCustomEvent(BRAZE_EVENT.ONBOARDING_START);
                const brazeUser = braze.getUser();

                if (brazeUser) {
                    brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.MARKETING_COMMUNITY);
                    brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.MARKETING_NEWSLETTERS);
                    brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.MARKETING_PROMOTIONS);
                    brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.TRANSACTIONAL_EDUCATION);
                    brazeUser.addToSubscriptionGroup(BRAZE_SUBSCRIPTION_GROUP.TRANSACTIONAL_REMINDERS);
                }
            }

            // Track in Heap
            heapTrack('Web Booking - Client Created');
        }

        updateClient(updatedClient);
        setLoading(false);
        goToNextStep();
    };

    const handleNext = async () => {
        // Validate phoneNumber one last time before submission
        if (!phoneNumberIsValid) {
            setPhoneNumberIsValid(isValidPhone(phoneNumber));
            if (!isValidPhone(phoneNumber)) {
                return null;
            }
        }

        // Check for existing client before proceeding
        await createNewClient();
        return null;
    };

    const selectAddress = async (prediction: Pick<google.maps.places.PlacePrediction, 'text' | 'placeId'>) => {
        setSelectedAddress(prediction);
        await placesInputFieldRef.current?.formatAddress(
            prediction,
            fullAddress.apartment,
            (formattedAddress) => {
                setFullAddress(formattedAddress);
                setAddressIsValid(true);
            },
            () => setAddressIsValid(false)
        );
    };

    const renderClientInputBody = () => {
        const SECTIONS: { title: string, input: ReactNode }[] = [{
            title: 'Full Name',
            input: (
                <>
                    <InputField
                        placeholder="First name"
                        value={firstName}
                        onChange={changeName('first')}
                        onBlur={() => firstName && validateNames('first')}
                        autoFocus={!firstName}
                        isInvalid={firstNameIsValid === false}
                    />
                    {firstNameIsValid === false && (
                        <Fonts.BodySmall
                            style={{ color: colors.RED }}
                        >
                            Your name must be 2 or more characters

                        </Fonts.BodySmall>
                    )}
                    <InputField
                        placeholder="Last name"
                        value={lastName}
                        onChange={changeName('last')}
                        onBlur={() => lastName && validateNames('last')}
                        isInvalid={lastNameIsValid === false}
                    />
                    {lastNameIsValid === false && (
                        <Fonts.BodySmall
                            style={{ color: colors.RED }}
                        >
                            Your name must be 2 or more characters

                        </Fonts.BodySmall>
                    )}
                </>
            )
        }, {
            title: 'Email Address',
            input: (
                <>
                    <InputField
                        placeholder="Email address"
                        value={email}
                        onChange={changeEmail()}
                        onBlur={() => email && setEmailIsValid(validate(email))}
                        isInvalid={emailIsValid === false}
                        inputMode="email"
                    />
                    {emailIsValid === false && (
                        <Fonts.BodySmall style={{ color: colors.RED }}>
                            Please enter a valid email address
                        </Fonts.BodySmall>
                    )}
                </>

            )
        }, {
            title: 'Phone Number',
            input: (
                <>
                    <PhoneInputContainer>
                        <CountryCode>US +1</CountryCode>
                        <InputField
                            placeholder="Phone number"
                            value={phoneNumber}
                            onChange={changePhoneNumber}
                            onBlur={validatePhoneNumber}
                            inputMode="tel"
                            isInvalid={phoneNumberIsValid === false}
                        />
                    </PhoneInputContainer>
                    {phoneNumberIsValid === false && (
                        <Fonts.BodySmall style={{ color: colors.RED }}>
                            Please enter a valid phone number
                        </Fonts.BodySmall>
                    )}
                </>
            )
        }, {
            title: 'Address',
            input: (
                <>
                    <PlacesInputField
                        key={`address-input-${locationShowCreditCardStep}-${googleMapsisLoaded}`}
                        ref={placesInputFieldRef}
                        placeholder={googleMapsisLoaded ? 'Address' : 'Loading...'}
                        hasChanged={hasFullAddressChanged(client.fullAddress, fullAddress)}
                        showError={addressIsValid === false}
                        selectedValue={selectedAddress}
                        onSelectResult={selectAddress}
                        types={['street_address']}
                    />
                    {addressIsValid === false && (
                        <Fonts.BodySmall style={{ color: colors.RED }}>Street address required</Fonts.BodySmall>
                    )}
                    <InputField
                        placeholder="Apartment number"
                        value={fullAddress.apartment}
                        onChange={(apartment) => setFullAddress({ ...fullAddress, apartment })}
                    />
                </>
            )
        }];

        return (
            <Body>
                <Fonts.Heading04>{"Let's get to know you"}</Fonts.Heading04>
                <ClientInputContainer>
                    {SECTIONS.filter((section) => section.title !== 'Address' || locationShowCreditCardStep).map((section) => (
                        <SectionContainer key={section.title}>
                            <Fonts.LabelMedium>{section.title}</Fonts.LabelMedium>
                            {section.input}
                        </SectionContainer>
                    ))}
                    <button
                        type="button"
                        onClick={() => setIsWhyInfoModalOpen(true)}
                        style={{ textAlign: 'left' }}
                    >
                        <Fonts.Body style={{ color: colors.PRIMARY_PERIWINKLE, textDecoration: 'underline' }}>
                            Why do we need this information?
                        </Fonts.Body>
                    </button>
                </ClientInputContainer>
            </Body>
        );
    };

    const renderClientInputButton = () => (
        <NextButtonContainer isMobile={isMobile}>
            <PrimaryButton
                id="client-onboarding-button"
                color="periwinkle"
                loading={loading}
                disabled={
                    !firstNameIsValid
                    || !lastNameIsValid
                    || !emailIsValid
                    || !phoneNumberIsValid
                    || (locationShowCreditCardStep && !addressIsValid)
                }
                onClick={handleNext}
            >
                Next
            </PrimaryButton>
        </NextButtonContainer>
    );

    const renderWhyInfoModal = () => (
        <Modal
            isModalOpen={isWhyInfoModalOpen}
            onCloseModal={() => setIsWhyInfoModalOpen(false)}
            shouldCloseOnOverlayClick
        >
            <ModalContainer>
                <Icons.WhyInfoDog height={91.73} width={120} />
                <ModalExitContainer onClick={() => setIsWhyInfoModalOpen(false)}>
                    <Icons.CircleGreyX height={24} width={24} />
                </ModalExitContainer>
                <Fonts.Heading06>Why do we need this info?</Fonts.Heading06>
                <Fonts.BodySmall style={{ marginTop: 12, marginBottom: 20 }}>
                    All of this information is required by law for us to create a medical record for your pet.
                    <br />
                    <br />
                    Modern Animal will never share your or your pet&apos;s data.
                </Fonts.BodySmall>
                <PrimaryButton color="black" onClick={() => setIsWhyInfoModalOpen(false)}>
                    Okay
                </PrimaryButton>
            </ModalContainer>
        </Modal>
    );

    return (
        <ClientInformationContainer>
            {renderClientInputBody()}
            {renderClientInputButton()}
            {renderWhyInfoModal()}
        </ClientInformationContainer>
    );
};

export default TestClientOnboarding;

const ClientInformationContainer = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-between;
    padding: 32px 24px 0 24px;
    gap: 8px;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const ClientInputContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin: 24px;
    gap: 8px;
    position: relative;
`;

const ModalExitContainer = styled.button`
    display: flex;
    position: absolute;
    top: 0;
    right: 0;
`;

const NextButtonContainer = styled.div<{ isMobile: boolean }>`
    display: flex;
    position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
    bottom: 0;
    background-color: ${colors.WHITE};
    padding: 16px 0 32px 0;
`;

const SectionContainer = styled(ClientInputContainer)`
    gap: 8px;
`;

const PhoneInputContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: 4px;
    align-items: center;
    width: 100%;
`;

const CountryCode = styled(Fonts.Heading06)`
    color: ${colors.GREY_2};
    padding: 0px 8px;
    white-space: nowrap;
`;
